import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown,
  Lock,
  ShieldCheck,
  KanbanSquare,
  ArrowRight,
  LineChart,
  Users,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const features = [
  { icon: KanbanSquare, title: "Visual Pipeline", desc: "Drag leads across New → Won on a Kanban board built for speed." },
  { icon: ShieldCheck, title: "MFA Enforced", desc: "Every account requires an authenticator app. No exceptions." },
  { icon: LineChart, title: "Deal Insights", desc: "Track pipeline value, follow-ups and stage breakdowns at a glance." },
  { icon: Users, title: "Team Ready", desc: "Shared lead visibility with admin-managed team access." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0B1120] text-white overflow-hidden">
      {/* Nav */}
      <header className="relative z-20 max-w-[1400px] mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="brand-logo">
          <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center">
            <Crown className="w-5 h-5 text-black" />
          </div>
          <div className="leading-tight">
            <p className="font-display font-extrabold text-lg tracking-tight">King of IT</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-500/80">CRM</p>
          </div>
        </div>
        <button
          data-testid="landing-login-button"
          onClick={() => navigate(user ? "/app" : "/login")}
          className="btn-primary rounded-lg px-6 py-2.5 text-sm inline-flex items-center gap-2"
        >
          <Lock className="w-4 h-4" />
          {user ? "Open CRM" : "Login"}
        </button>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]" />

        <div className="relative max-w-[1400px] mx-auto px-6 pt-16 pb-24 lg:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-semibold text-amber-400 mb-6">
              <ShieldCheck className="w-3.5 h-3.5" /> Internal tool · crm.kingofit.ca
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              The command center for the <span className="text-gold-gradient">King of IT</span> sales pipeline.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              A private, secure workspace to track prospects from first outreach to closed
              deal — protected by mandatory multi-factor authentication.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button
                data-testid="hero-login-button"
                onClick={() => navigate(user ? "/app" : "/login")}
                className="btn-primary rounded-lg px-7 py-3 inline-flex items-center gap-2"
              >
                {user ? "Open Dashboard" : "Secure Login"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>

          {/* Feature grid */}
          <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                className="rounded-xl border border-slate-800 bg-[#131C31] p-6 hover:border-amber-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="font-display font-bold text-lg">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-8 text-center text-sm text-slate-500">
        King of IT — Toronto IT Support & Managed Services. Internal use only.
      </footer>
    </div>
  );
}
